var _InputAdornment;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, exact, func, number, object, oneOf, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import { CheckCircleOutline, RadioButtonUnchecked, Search as ActionSearch, UnfoldMore } from 'mosaic-react-icons';
import { textFieldClasses } from '@mui/material/TextField';
import { inputBaseClasses } from '@mui/material/InputBase';
import Popover, { popoverClasses } from '@mui/material/Popover';
import Button, { buttonClasses } from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, styled, useTheme } from '@mui/material/styles';
import cloneDeep from 'lodash.clonedeep';
import { fontSize } from '../sovos-navigation/constants/styles';
import SovosFixedWidthText from '../sovos-fixed-width-text';
import SovosTooltip from '../sovos-tooltip';
import SovosTypography from '../sovos-typography';
import { SovosTreeList } from '../sovos-tree-list';
import SovosTextField from '../sovos-text-field';
import SovosResizeContainer from '../sovos-resize-container';
import { collapsedWidthFactor } from '../themes/sovos-sizes';
import { spacingToNumber } from '../internals/utils';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const findRecursively = (data, nestedProperty, comparisonFunction) => {
  for (let i = 0; i < data.length; i += 1) {
    if (comparisonFunction(data[i])) {
      return data[i];
    }
    if (data[i][nestedProperty]) {
      const foundItem = findRecursively(data[i][nestedProperty], nestedProperty, comparisonFunction);
      if (foundItem) return foundItem;
    }
  }
  return undefined;
};
const hasValidId = account => {
  if (!account) return false;
  if (!('id' in account)) return false;
  const {
    id
  } = account;
  if (typeof id === 'string') {
    if (id === '') return false;
    return true;
  }
  if (typeof id === 'number') return true;
  return false;
};
export const types = {
  PRODUCTION: 'production',
  TEST: 'test',
  DEFAULT: 'default'
};
const getAccountColors = palette => ({
  production: palette.success.main,
  test: palette.grey.A200,
  default: 'transparent'
});
const getIconStyles = spacing => ({
  width: spacing(2),
  height: spacing(2),
  marginLeft: spacing(0.5),
  alignSelf: 'center'
});
const fallbackAccounts = [{
  name: 'error'
}];
const ToggleButton = styled(Button)(_ref => {
  let {
    theme: {
      palette
    }
  } = _ref;
  return {
    borderRadius: 0,
    padding: 0,
    textTransform: 'none',
    width: '100%',
    ["&.".concat(buttonClasses.focusVisible)]: {
      color: alpha(palette.primary.contrastText, 0.4)
    }
  };
});
const StyledPopover = styled(Popover)(_ref2 => {
  let {
    theme: {
      spacing
    }
  } = _ref2;
  return {
    ["& .".concat(popoverClasses.paper)]: {
      maxWidth: spacing(60),
      minWidth: spacing(30),
      paddingTop: spacing(2),
      paddingBottom: spacing()
    }
  };
});
const ButtonContents = styled('div', {
  shouldForwardProp: prop => prop !== 'isButtonClickable' && prop !== 'selectedAccount'
})(_ref3 => {
  let {
    isButtonClickable,
    selectedAccount,
    theme: {
      palette,
      shape: themeShape,
      spacing,
      typography,
      transitions: {
        duration: {
          short: transitionDuration
        }
      }
    }
  } = _ref3;
  const getBorderColor = () => {
    const accountColors = getAccountColors(palette);
    return selectedAccount ? accountColors[selectedAccount.type || 'default'] : accountColors.default;
  };
  return {
    '&.SovosAccountSwitcher': {
      '&__buttonContents': {
        ...typography.body1,
        alignItems: 'center',
        backgroundColor: alpha(palette.primary.light, 0.08),
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: getBorderColor(),
        borderRadius: themeShape.borderRadius,
        color: palette.primary.contrastText,
        cursor: isButtonClickable ? 'pointer' : 'default',
        display: 'flex',
        fontSize,
        fontWeight: 400,
        height: spacing(5),
        justifyContent: undefined,
        padding: "0 ".concat(spacing(), " 0 ").concat(spacing(2.5)),
        transition: "background-color ".concat(transitionDuration, "ms"),
        width: '100%',
        '&:hover': {
          backgroundColor: alpha(palette.primary.light, 0.2)
        },
        '&--minimized': {
          justifyContent: 'center',
          padding: 0,
          marginRight: 0
        }
      }
    },
    '& .SovosAccountSwitcher': {
      '&__buttonContentsLabel': {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        lineHeight: 1.2,
        textAlign: 'left',
        width: '100%',
        '& .icon': {
          ...getIconStyles(spacing)
        }
      },
      '&__name': {
        flexGrow: '1'
      },
      '&__taxYear': {
        fontWeight: typography.fontWeightBold
      },
      '&__dropdownIndicator': {
        height: spacing(2.5),
        width: spacing(2.5)
      }
    }
  };
});
const StyledMenuItem = styled('div')(_ref4 => {
  let {
    theme: {
      spacing
    }
  } = _ref4;
  return {
    display: 'flex',
    flexGrow: 1,
    maxWidth: '100%',
    '& .text': {
      paddingRight: spacing(1.5),
      flexGrow: 1
    },
    '& .icon': {
      ...getIconStyles(spacing)
    }
  };
});
const SearchWrapper = styled('div')(_ref5 => {
  let {
    theme: {
      spacing
    }
  } = _ref5;
  return {
    margin: "0 ".concat(spacing(), " ").concat(spacing(), " ").concat(spacing())
  };
});
const Search = styled(SovosTextField)(_ref6 => {
  let {
    theme: {
      spacing
    }
  } = _ref6;
  return {
    ["&.".concat(textFieldClasses.root)]: {
      minHeight: 'auto'
    },
    ["& .".concat(inputBaseClasses.adornedStart)]: {
      paddingLeft: spacing()
    }
  };
});
const TreeListWrapper = styled('div')({
  maxHeight: 'calc(100vh - 240px)',
  overflow: 'auto'
});
const NoResults = styled('div')(_ref7 => {
  let {
    theme: {
      palette,
      spacing,
      typography
    }
  } = _ref7;
  return {
    ...typography.body1,
    alignItems: 'center',
    color: palette.text.secondary,
    display: 'flex',
    height: spacing(5),
    justifyContent: 'center',
    margin: spacing()
  };
});
const generateId = account => "".concat(account.name).concat(account.taxYear || '').concat(account.type || '');
const SovosAccountSwitcher = _ref8 => {
  let {
    accounts: accountsProp,
    classes,
    className,
    didAccountChange,
    isDisabled = false,
    isLoading = false,
    isMinimal = false,
    labels,
    minimumSearchCharacters = 2,
    onExpandAccount,
    onSearch,
    PopoverProps,
    selectedAccount: selectedAccountProp,
    setAccount,
    SwitcherClasses,
    ...rest
  } = _ref8;
  const {
    t
  } = useMosaicTranslation();
  const {
    palette,
    spacing
  } = useTheme();
  const accountColors = useMemo(() => getAccountColors(palette), [palette]);
  const externalSearch = !!onSearch;
  const areAccountsValid = Array.isArray(accountsProp) && (externalSearch || !!accountsProp.length && !!accountsProp[0].name);
  const accounts = useMemo(() => areAccountsValid ? accountsProp : fallbackAccounts, [accountsProp, areAccountsValid]);
  const testLbl = (labels === null || labels === void 0 ? void 0 : labels.test) || t('accountSwitcher.test');
  const prodLbl = (labels === null || labels === void 0 ? void 0 : labels.production) || t('accountSwitcher.production');
  const renderAccountIcon = useCallback((account, color) => {
    const {
      type
    } = account;
    let Icon;
    let label;
    switch (type) {
      case types.TEST:
        Icon = RadioButtonUnchecked;
        label = testLbl;
        break;
      case types.PRODUCTION:
        Icon = CheckCircleOutline;
        label = prodLbl;
        break;
      default:
        return null;
    }
    return /*#__PURE__*/React.createElement(SovosTooltip, {
      title: label
    }, /*#__PURE__*/React.createElement(Icon, {
      classes: {
        root: classes === null || classes === void 0 ? void 0 : classes.icon
      },
      className: "icon",
      style: {
        color
      }
    }));
  }, [classes, prodLbl, testLbl]);
  const accountData = useMemo(() => {
    const getItemContent = account => {
      const {
        name,
        taxYear,
        type
      } = account;
      return /*#__PURE__*/React.createElement(StyledMenuItem, {
        className: classes === null || classes === void 0 ? void 0 : classes.menuItem
      }, /*#__PURE__*/React.createElement(SovosFixedWidthText, {
        text: name,
        className: clsx('text', classes === null || classes === void 0 ? void 0 : classes.text)
      }), taxYear && /*#__PURE__*/React.createElement("span", null, taxYear), !!type && renderAccountIcon(account, accountColors[type]));
    };
    const getMenuItem = account => {
      const {
        nestedAccounts,
        nestedAccountCount,
        ...accountProps
      } = account;
      const item = {
        id: hasValidId(account) ? account.id : generateId(account),
        content: getItemContent(account),
        nestedItemCount: nestedAccountCount,
        ...accountProps
      };

      // SovosTreeList has its own types and these would conflict
      if (item.type) {
        item.accountType = item.type;
        delete item.type;
      }
      if (nestedAccounts) {
        item.nestedItems = nestedAccounts.map(nestedAccount => getMenuItem(nestedAccount));
      }
      return item;
    };
    return accounts.length ? cloneDeep(accounts).map(getMenuItem) : [];
  }, [classes, accountColors, accounts, renderAccountIcon]);
  const [searchInput, setSearchInput] = useState('');
  const [filteredItems, setFilteredItems] = useState(accountData);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isButtonClickable = externalSearch || accounts.length > 1 || accounts[0].nestedAccounts;
  const searchMessage = "".concat(minimumSearchCharacters, " ").concat((labels === null || labels === void 0 ? void 0 : labels.searchTemplate) || t('autocomplete.helpMessageTemplate'));
  useEffect(() => {
    // If the items have changed or the loading flag has changed, clear the search trigger
    if (isLoading || accountData) {
      setSearchTriggered(false);
      setFilteredItems(accountData);
    }
  }, [accountData, isLoading]);
  const getItems = () => {
    if (onSearch) {
      return accountData;
    }
    return filteredItems;
  };
  const selectedAccount = useMemo(() => {
    const shallowAccountComparison = account => (hasValidId(account) ? account.id === selectedAccountProp.id : true) || account.name === selectedAccountProp.name && account.taxYear === selectedAccountProp.taxYear && account.type === selectedAccountProp.type;
    const accountComparison = typeof didAccountChange === 'function' ? account => !didAccountChange(account, selectedAccountProp) : shallowAccountComparison;
    const isInArray = !externalSearch && selectedAccountProp && !!findRecursively(accounts, 'nestedAccounts', accountComparison);
    const isValid = (selectedAccountProp === null || selectedAccountProp === void 0 ? void 0 : selectedAccountProp.name) && typeof selectedAccountProp.name === 'string';
    if (!isValid) {
      console.error('SovosAccountSwitcher received an invalid selectedAccount');
    }
    if (!externalSearch && !isInArray) {
      console.error('SovosAccountSwitcher did not find selectedAccount in accounts array');
    }
    const validAccount = externalSearch || isInArray || isValid && !areAccountsValid ? selectedAccountProp : accounts[0];
    if (hasValidId(validAccount)) return validAccount;
    return {
      id: generateId(validAccount),
      ...validAccount
    };
  }, [areAccountsValid, accounts, didAccountChange, externalSearch, selectedAccountProp]);
  const handleSetAccount = item => {
    const accountMatcher = account => item.id === account.id;
    const newSelectedAccount = findRecursively(accountData, 'nestedItems', accountMatcher);
    const result = {
      ...newSelectedAccount
    };
    if (result.accountType) {
      result.type = result.accountType;
    }
    delete result.accountType;
    delete result.content;
    delete result.nestedItems;
    setAccount(result);
  };
  const itemMatches = (item, value) => {
    const itemName = item.name.toUpperCase();
    const searchString = value.toUpperCase();
    return itemName.includes(searchString);
  };
  const getExpandedIds = () => {
    // Get items that have been filtered
    const listItems = getItems();
    if (!listItems || !listItems.length || !searchInput || searchTriggered) {
      return undefined;
    }
    const itemsToExpand = new Set();
    const getItemsToExpand = item => {
      let result = false;
      if (itemMatches(item, searchInput) && !item.nestedItems) {
        result = true;
      }
      if (item.nestedItems) {
        const filtered = item.nestedItems.filter(nested => getItemsToExpand(nested, result) || itemMatches(nested, searchInput));
        if (filtered.length) result = true;
      }
      if (result) itemsToExpand.add(item.id);
      return result;
    };
    listItems.forEach(item => getItemsToExpand(item));
    return Array.from(itemsToExpand);
  };

  // Filter search values, calculating items to include and items to expand.
  const search = value => {
    if (!value) {
      setFilteredItems(accountData);
      return;
    }
    if (value.length < minimumSearchCharacters) return;
    if (onSearch) {
      onSearch(value);
      return;
    }
    const filteredResults = arr => {
      const filtered = arr.reduce((matches, item) => {
        const {
          nestedItems
        } = item;
        if (itemMatches(item, value)) {
          matches.push(item);
        } else if (nestedItems && nestedItems.length) {
          const nestedResults = filteredResults(nestedItems);
          if (nestedResults.length) {
            matches.push({
              ...item,
              nestedItems: nestedResults
            });
          }
        }
        return matches;
      }, []);
      return filtered;
    };
    const results = filteredResults(accountData);
    setFilteredItems(results);
    setSearchTriggered(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchValue = useCallback(debounce(value => search(value), 500), [accounts]);
  const handleSearchInput = event => {
    const {
      value
    } = event.target;
    setSearchInput(value);
    if (onSearch && value.length >= minimumSearchCharacters) {
      setSearchTriggered(true);
    } else if (!onSearch) {
      setSearchTriggered(true);
    }
    handleSearchValue(value);
  };
  const handleButtonClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleSelectItem = item => {
    const selectItemChanged = (currentSelection, newSelection) => currentSelection.id !== newSelection.id;
    const itemComparison = didAccountChange !== null && didAccountChange !== void 0 ? didAccountChange : selectItemChanged;
    if (itemComparison(selectedAccount, item)) {
      handleSetAccount(item);
    }
    setAnchorEl(null);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const renderNoResults = () => {
    let message;
    if (isLoading || searchTriggered) {
      message = (labels === null || labels === void 0 ? void 0 : labels.loading) || t('autocomplete.loadingMessage');
    } else if (onSearch && (!searchInput || searchInput.length < minimumSearchCharacters)) {
      message = searchMessage;
    } else {
      message = (labels === null || labels === void 0 ? void 0 : labels.noResults) || t('noResults');
    }
    return /*#__PURE__*/React.createElement(NoResults, {
      className: clsx('sovosAccountSwitcher__noResults', classes === null || classes === void 0 ? void 0 : classes.noResults)
    }, message);
  };
  const buttonContents = useMemo(() => {
    const showDropdown = !selectedAccount.taxYear && !selectedAccount.type && isButtonClickable;
    const contentProps = {
      'data-testid': 'SovosAccountSwitcher__buttonContents',
      isButtonClickable,
      selectedAccount
    };
    const renderTaxYear = () => /*#__PURE__*/React.createElement(SovosTypography, {
      variant: isMinimal ? 'body2' : 'body1',
      className: "SovosAccountSwitcher__year"
    }, selectedAccount && selectedAccount.taxYear.substring(0, 2), /*#__PURE__*/React.createElement("span", {
      className: clsx('SovosAccountSwitcher__taxYear', classes === null || classes === void 0 ? void 0 : classes.taxYear)
    }, selectedAccount && selectedAccount.taxYear.substring(2)));
    return isMinimal ? /*#__PURE__*/React.createElement(ButtonContents, _extends({
      className: clsx('SovosAccountSwitcher__buttonContents', 'SovosAccountSwitcher__buttonContents--minimized', classes === null || classes === void 0 ? void 0 : classes.buttonContentsMinimized, classes === null || classes === void 0 ? void 0 : classes.buttonContents)
    }, contentProps), selectedAccount.taxYear ? renderTaxYear() : /*#__PURE__*/React.createElement(UnfoldMore, {
      className: clsx('SovosAccountSwitcher__dropdownIndicator', classes === null || classes === void 0 ? void 0 : classes.dropdownIndicator),
      "data-testid": "SovosAccountSwitcher__dropdown-indicator"
    })) : /*#__PURE__*/React.createElement(ButtonContents, _extends({
      className: clsx('SovosAccountSwitcher__buttonContents', classes === null || classes === void 0 ? void 0 : classes.buttonContents)
    }, contentProps), /*#__PURE__*/React.createElement("div", {
      className: clsx('SovosAccountSwitcher__buttonContentsLabel', classes === null || classes === void 0 ? void 0 : classes.buttonLabel)
    }, /*#__PURE__*/React.createElement(SovosFixedWidthText, {
      text: selectedAccount.name,
      className: clsx('SovosAccountSwitcher__name', classes === null || classes === void 0 ? void 0 : classes.name)
    }), selectedAccount.taxYear && renderTaxYear(), renderAccountIcon(selectedAccount, selectedAccount.type && accountColors[selectedAccount.type].text), showDropdown && /*#__PURE__*/React.createElement(UnfoldMore, {
      className: clsx('SovosAccountSwitcher__dropdownIndicator', classes === null || classes === void 0 ? void 0 : classes.dropdownIndicator),
      "data-testid": "SovosAccountSwitcher__dropdown-indicator"
    })));
  }, [classes, accountColors, isButtonClickable, isMinimal, renderAccountIcon, selectedAccount]);
  const renderPopover = () => {
    const expanded = getExpandedIds();
    const listItems = getItems();
    return /*#__PURE__*/React.createElement(StyledPopover, _extends({
      anchorOrigin: {
        horizontal: isMinimal ? spacingToNumber(spacing(collapsedWidthFactor)) : 0,
        vertical: isMinimal ? 'top' : 'bottom'
      },
      anchorEl: anchorEl,
      classes: {
        paper: classes === null || classes === void 0 ? void 0 : classes.popoverPaper
      },
      className: clsx('sovosAccountSwitcher__popover', 'sovosAccountSwitcher__popover--showSearch'),
      "data-testid": "sovosAccountSwitcher__popover",
      marginThreshold: 0,
      onClose: handleClose,
      open: !!anchorEl,
      transformOrigin: {
        horizontal: 'left',
        vertical: 'top'
      }
    }, PopoverProps), /*#__PURE__*/React.createElement(SovosResizeContainer, null, /*#__PURE__*/React.createElement(SearchWrapper, {
      className: classes === null || classes === void 0 ? void 0 : classes.search
    }, /*#__PURE__*/React.createElement(Search, {
      autoFocus: true,
      fullWidth: true,
      InputProps: {
        classes: {
          adornedStart: classes === null || classes === void 0 ? void 0 : classes.searchFieldAdornment
        },
        startAdornment: _InputAdornment || (_InputAdornment = /*#__PURE__*/React.createElement(InputAdornment, {
          position: "start"
        }, /*#__PURE__*/React.createElement(ActionSearch, null)))
      },
      onChange: handleSearchInput,
      placeholder: t('search'),
      size: "small",
      value: searchInput,
      variant: "outlined"
    })), listItems && listItems.length && !(onSearch && (isLoading || searchTriggered)) ? /*#__PURE__*/React.createElement(TreeListWrapper, {
      className: classes === null || classes === void 0 ? void 0 : classes.treeListWrapper
    }, /*#__PURE__*/React.createElement(SovosTreeList, {
      key: expanded && expanded.toString(),
      data: listItems,
      initialExpandedItems: expanded,
      initialSelectedItem: selectedAccount.id,
      onItemClick: handleSelectItem,
      onItemExpanded: onExpandAccount
    })) : renderNoResults()));
  };
  const renderButton = () => {
    const button = /*#__PURE__*/React.createElement(ToggleButton, {
      classes: {
        root: classes === null || classes === void 0 ? void 0 : classes.toggleButton,
        focusVisible: classes === null || classes === void 0 ? void 0 : classes.toggleButtonFocus
      },
      className: "sovosAccountSwitcher__button",
      "data-testid": "sovosAccountSwitcher__button",
      disabled: isDisabled,
      fullWidth: true,
      onClick: handleButtonClick
    }, buttonContents);
    if (isMinimal && !isDisabled) {
      return /*#__PURE__*/React.createElement(SovosTooltip, {
        title: selectedAccount.name
      }, /*#__PURE__*/React.createElement("div", null, button));
    }
    return button;
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    className: clsx('sovosAccountSwitcher', className, classes === null || classes === void 0 ? void 0 : classes.root)
  }, rest), renderButton(), renderPopover());
};
const accountItemShape = {
  disabled: bool,
  id: oneOfType([number, string]),
  isLoading: bool,
  name: string.isRequired,
  nestedAccountCount: number,
  taxYear: string,
  type: oneOf(Object.values(types))
};
accountItemShape.nestedAccounts = arrayOf(shape(accountItemShape));
const accountItemPropTypes = shape(accountItemShape);
SovosAccountSwitcher.propTypes = {
  /**
   * Array of objects, each with
   *
   * - `id` string or number. Required if name + taxYear + type may not be unique
   * - `name` string
   * - 'taxYear` string
   * - `type` string, one of , 'production', or 'test', or undefined
   * - `disabled` bool
   * - `nestedAccounts` array of objects
   */
  accounts: arrayOf(accountItemPropTypes).isRequired,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Override or extend the styles applied to the component
   */
  className: string,
  /**
   * Custom account equality check
   */
  didAccountChange: func,
  /**
   * If `true`, button is disabled
   */
  isDisabled: bool,
  /**
   * If `true`, `onSearch` is loading results
   */
  isLoading: bool,
  /**
   * If `true`, component is rendered in minimal state
   */
  isMinimal: bool,
  /**
   * Custom labels for account types and searching, should only be used for
   * internationalization
   */
  labels: exact({
    loading: string,
    noResults: string,
    production: string,
    searchTemplate: string,
    test: string
  }),
  /**
   * Minimum number of characters needed to trigger an asynchronous account search
   */
  minimumSearchCharacters: number,
  /**
   * Callback fired when a account is expanded. Used to asynchronously load
   * nested accounts `function(account: object) => void`
   */
  onExpandAccount: func,
  /**
   * Callback fired when text is entered into the search box. Used to
   * asynchronously search accounts. If `onSearch` is provided,
   * `selectedAccount` is required. `function(searchVal: string) => void`
   */
  onSearch: props => {
    const {
      onSearch,
      selectedAccount
    } = props;
    if (onSearch && typeof onSearch !== 'function') {
      return new Error('onSearch must be a function.');
    }
    if (onSearch && !selectedAccount) {
      return new Error('When onSearch is passed in, selectedAccount is required.');
    }
    return null;
  },
  /**
   * Properties forwarded to the Popover menu
   */
  PopoverProps: object,
  /**
   * When missing, invalid, or not in the accounts array, the first account
   * will be selected.
   */
  selectedAccount: accountItemPropTypes,
  /**
   * Callback fired when a new account is selected
   */
  setAccount: func.isRequired,
  /**
   * Override or extend the styles applied to the Switcher component
   */
  SwitcherClasses: object
};
export default SovosAccountSwitcher;